import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import Login from './components/Login';
import Dashboard from './components/Dashboard';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div style={{ textAlign: 'center', marginTop: '50px' }}>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log('Checking auth at:', process.env.REACT_APP_BACKEND_URL + '/auth/me');
        const response = await fetch(process.env.REACT_APP_BACKEND_URL + '/auth/me', {
          credentials: 'include',
        });
        console.log('Auth response status:', response.status);

        if (response.ok) {
          const userData = await response.json();
          console.log('User authenticated:', userData);
          setUser(userData);

          // If on root and authenticated, redirect to dashboard
          if (window.location.pathname === '/') {
            navigate('/dashboard', { replace: true });
          }
        } else {
          const text = await response.text();
          console.error('Auth failed with status:', response.status, 'Response:', text.substring(0, 200));
          setUser(null);
        }
      } catch (error) {
        console.error("Authentication check failed:", error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    // Close menu when clicking outside
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('button[aria-label="Menu"]')) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  const handleAuthAction = () => {
    if (user) {
      // Logout - clear local state and redirect to oauth2 sign out
      setUser(null);
      window.location.href = '/oauth2/sign_out?rd=' + encodeURIComponent('/');
    } else {
      // Login
      window.location.href = '/oauth2/sign_in?rd=' + encodeURIComponent('/dashboard');
    }
  };

  const authValue = { user, loading, setUser };

  if (loading) {
    return <div style={{ textAlign: 'center', marginTop: '50px' }}>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={authValue}>
      <div className="App">
        {/* Global Menu */}
        <div style={{ position: 'absolute', top: '10px', left: '10px', zIndex: 2000 }}>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            style={{
              fontSize: '28px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              lineHeight: '1',
              filter: 'grayscale(20%) brightness(0.9)'
            }}
            aria-label="Menu"
            title={user ? 'Logout' : 'Login'}
          >
            <img src="/logo512.png" alt="Logo" style={{ height: '32px', filter: 'grayscale(20%) brightness(0.9)' }} />
          </button>
          {isMenuOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '5px',
              background: 'linear-gradient(135deg, #3d3127 0%, #2a2018 100%)',
              border: '2px solid #5a4a38',
              borderRadius: '4px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
              zIndex: 2001,
              minWidth: '150px'
            }}>
              <button
                onClick={handleAuthAction}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  background: 'none',
                  border: 'none',
                  color: '#f9f6f0',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '14px',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = 'rgba(218, 165, 32, 0.2)'}
                onMouseOut={(e) => e.target.style.background = 'none'}
              >
                {user ? 'Logout' : 'Login'}
              </button>
            </div>
          )}
        </div>

        <Routes>
          <Route path="/" element={<Login />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard user={user} />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </AuthContext.Provider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;

